import React, { useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';
import { Card, PokerVariant } from '@/services/table/tableTypes';
import useIsMobileView from '@/hooks/useIsMobileView';
import { Pot, WinningData } from '@/services/table/tableState';
import CardFace, { CardSizes } from '@/components/CardFace/CardFace';
import MixGameRoundLabel from '@/features/poker-table/components/MixGameRoundLabel/MixGameRoundLabel';

import { checkWinningCard } from '../../utils/checkWinningCard';
import CardHolder from '../CardHolder/CardHolder';
import TotalPot from '../TotalPot/TotalPot';
import styles from './Board.module.css';
import PokerChipStacks from '../PokerChips/PokerChipStacks';

// TODO: add when fixing animations
// import { useSelector } from 'react-redux';
// import { ReducerState } from '@/services/common/lib/reducerState';
// import { usePositionDispatch, usePositionStore } from '../../context/PositionContext';
// import AnimatedPokerChipStacks from '../PokerChips/AnimatedPokerChips';
// import { ChipsPushing } from '@/services/table/tableOperations';
// import { createSelector } from '@reduxjs/toolkit';
// import calculatAnimationTargetPosition from '../../utils/calculatAnimationTargetPosition';
// import { setPotRef } from '../../context/positionActions';

// TODO: add when fixing animations
// Assuming state structure and types are correctly defined elsewhere
// const selectTableActionPerformed = (state: ReducerState, tableId: string) => state.tables?.[tableId]?.actionPerformed;

// TODO: add when fixing animations
// const selectShouldAnimate = createSelector([selectTableActionPerformed], (actionPerformed) => {
//   const chipsPushingAction = actionPerformed as ChipsPushing;
//   const amounts = chipsPushingAction?.amounts?.reduce((acc, amount) => acc + amount, 0);
//   return chipsPushingAction?.subtype === 'ChipsPushing' && amounts > 0;
// });
// const selectPotsToPush = createSelector([selectTableActionPerformed], (actionPerformed) => {
//   const chipsPushingAction = actionPerformed as ChipsPushing;
//   if (chipsPushingAction?.subtype === 'ChipsPushing') {
//     return chipsPushingAction?.amounts;
//   }
// });

interface BoardProps {
  isLastStreet: boolean;
  isDrawStreet: boolean;
  cards: Card[];
  winnerName?: string;
  winnerBet?: number;
  boardCardsCount: number;
  currentVariant: string;
  game: string;
  variants: PokerVariant[];
  currentDraw?: number;
  totalDraws?: number;
  handsInCurrentGame: number;
  handsPerGame: number;
  pot: number;
  pots?: Pot[];
  bigBlind: number;
  winningData?: WinningData;
  isInitializing: boolean;
  tableId: string;
  numberOfPlayers: number;
}

const Board: React.FC<BoardProps> = ({
  isLastStreet,
  isDrawStreet,
  cards,
  winnerName,
  currentVariant,
  boardCardsCount,
  variants,
  currentDraw,
  totalDraws,
  handsInCurrentGame,
  handsPerGame,
  pots,
  bigBlind,
  winningData,
  isInitializing,
  tableId,
  numberOfPlayers,
}) => {
  // Refs
  const boardRef = useRef<HTMLDivElement>(null);

  // Hooks

  // TODO: add when fixing animations
  // const dispatch = usePositionDispatch();
  const isMobileView = useIsMobileView({
    checkForUserAgent: true,
  });

  // Selectors
  // TODO: add when fixing animations
  // const shouldAnimate = useSelector((state: ReducerState) => selectShouldAnimate(state, tableId));
  // const potsToPush = useSelector((state: ReducerState) => selectPotsToPush(state, tableId));

  // TODO: add when fixing animations
  // Context
  // const {
  //   state: { tableRef, userChipsRefs },
  // } = usePositionStore();

  const cardHolders = useMemo(() => {
    const holders = [];
    for (let i = cards.length; i < boardCardsCount; i++) {
      holders.push(i);
    }
    return holders;
  }, [cards.length, boardCardsCount]);

  const drawLabel = useMemo(() => {
    if (totalDraws !== undefined && currentDraw !== undefined) {
      if (isLastStreet) return 'NO MORE DRAWS';
      if (totalDraws === currentDraw) return 'LAST DRAW';
      const remainingDraws = totalDraws - currentDraw + (isDrawStreet ? 1 : 0);
      if (remainingDraws === 1) return '1 DRAW REMAINING';
      return `${remainingDraws} DRAWS REMAINING`;
    }
  }, [currentDraw, isDrawStreet, isLastStreet, totalDraws]);

  // Effects

  // TODO: add when fixing animations
  // useEffect(() => {
  //   if (boardRef?.current) {
  //     dispatch(setPotRef(boardRef));
  //   }
  // }, [dispatch, boardRef]);

  // Variables
  const isMixedGame = variants.length > 1;
  const winningCards = winningData?.cards;
  const handMessages = winningData?.handMessages;
  const totalPot = winningData?.pot;
  const isLastHand = handsInCurrentGame === handsPerGame;
  const showTransition = !!(isLastHand && winningData);

  // TODO: add when fixing animations
  // const [winnerPositions, setWinnerPositions] = useState<Record<number, { x: number; y: number }>>({});

  // TODO: add when fixing animations
  // New selector to get the winner information
  // const selectWinnerInfo = createSelector([selectTableActionPerformed], (actionPerformed) => {
  //   const chipsPushingAction = actionPerformed as ChipsPushing;
  //   if (chipsPushingAction?.subtype === 'ChipsPushing') {
  //     return chipsPushingAction.info;
  //   }
  // });

  // TODO: add when fixing animations
  // const winnerInfo = useSelector((state: ReducerState) => selectWinnerInfo(state, tableId));

  // TODO: add when fixing animations
  // useEffect(() => {
  //   if (winnerInfo && userChipsRefs) {
  //     const newWinnerPositions: Record<number, { x: number; y: number }> = {};
  //     winnerInfo.forEach((potInfo) => {
  //       potInfo.players.forEach((playerInfo) => {
  //         if (playerInfo.amount > 0) {
  //           const winnerRef = userChipsRefs[playerInfo.player_index];
  //           if (winnerRef && tableRef && boardRef.current) {
  //             newWinnerPositions[playerInfo.player_index] = calculatAnimationTargetPosition(
  //               tableRef,
  //               boardRef,
  //               winnerRef,
  //             );
  //           }
  //         }
  //       });
  //     });
  //     setWinnerPositions(newWinnerPositions);
  //   }
  // }, [winnerInfo, userChipsRefs, tableRef]);

  return (
    <div
      className={twMerge(
        'grid w-full items-center justify-center gap-3',
        styles.boardContainer,
        isMobileView ? '!h-[50em]' : '',
      )}
    >
      {/* TODO: Improve winning animation and visual cues. */}
      {/* {winnerName ? (
        <div className={twMerge(styles.draws, "flex justify-center")}>
          <p className="text-base font-bold text-ph-orange-secondary px-2.5 py-0.5 rounded-md uppercase">
            {winnerName} wins! {isMobileView && <br />}
            Pot: $
            {user.settings?.stackInBigBlindsEnabled && bigBlind
              ? `${winnerBet! / bigBlind} BB`
              : formatCurrency(winnerBet!)}
          </p>
        </div>
      ) : ( */}
      <div className={twMerge('flex justify-center', numberOfPlayers === 8 ? styles.cards : styles.pot)}>
        <div className={twMerge('flex flex-col items-center justify-center gap-0', isMobileView ? 'text-[10px]' : '')}>
          <TotalPot tableId={tableId} />
          <div ref={boardRef} className="mt-2 flex justify-center space-x-2">
            {!totalPot &&
              pots?.map((sidePot, index) => (
                <PokerChipStacks key={index} amount={sidePot.unraked_amount} bigBlind={bigBlind} tableId={tableId} />
              ))}
            {/* {potsToPush?.map((pot, index) => {
              if (pot === 0) return null;
              const winnerPosition = winnerPositions[index];
              return (
                <AnimatedPokerChipStacks
                  key={index}
                  amount={pot}
                  bigBlind={bigBlind}
                  toPosition={winnerPosition || { x: 0, y: 0 }}
                  fromPosition={{ x: 0, y: 0 }}
                  shouldAnimate={shouldAnimate && !!winnerPosition}
                  tableId={tableId}
                  skipAnimation
                />
              );
            })} */}
          </div>
        </div>
      </div>
      {totalDraws !== undefined && currentDraw !== undefined && !winnerName && totalDraws > 0 && (
        <div className={twMerge(styles.draws, isMobileView ? 'text-[10px]' : '')}>
          <span className={twMerge('flex h-5 justify-center text-base font-bold text-ph-orange-secondary')}>
            {drawLabel}
          </span>
        </div>
      )}

      {isInitializing && (
        <span
          className={twMerge('flex h-5 justify-center text-base font-bold text-white text-opacity-50', styles.draws)}
        >
          Initializing
          <span className={styles.dot1}>.</span>
          <span className={styles.dot2}>.</span>
          <span className={styles.dot3}>.</span>
        </span>
      )}

      <div className={twMerge('flex justify-center gap-1', numberOfPlayers === 8 ? styles.pot : styles.cards)}>
        {cards.map((card: Card, index: number) => {
          const isWinnerCard = winningCards && checkWinningCard(card, winningCards);

          return (
            <motion.div
              initial={{ x: -17, zoom: 1.04 }}
              animate={{ x: 0, zoom: isWinnerCard ? 1.1 : 1 }}
              transition={{ duration: isWinnerCard ? 0.1 : 0.45 }}
            >
              <CardFace
                key={`board-card-${index}`}
                rank={card.rank}
                suit={card.suit}
                size={isMobileView ? CardSizes.lg : CardSizes.xl}
                isWinner={isWinnerCard}
              />
            </motion.div>
          );
        })}
        {/* We need to add the card holders to the board but not show them to keep the space the cards would occupy */}
        <div className="invisible flex justify-start gap-1">
          {cardHolders.map((cardHolder) => (
            <CardHolder key={cardHolder} size={isMobileView ? CardSizes.lg : CardSizes.xl} />
          ))}
        </div>
      </div>

      <div className={styles.game}>
        {isMixedGame && (
          <div
            className={twMerge(
              'flex justify-center',
              !isLastHand && handMessages ? 'opacity-30' : '',
              isMobileView ? 'text-[10px]' : '',
            )}
          >
            <MixGameRoundLabel
              currentGameMode={currentVariant}
              round={handsInCurrentGame}
              roundsPerGame={handsPerGame}
              variants={variants}
              showTransition={showTransition}
              className={twMerge(showTransition && '-mt-20')}
            />
          </div>
        )}

        {/* {(isMobileView || !isMobileViewLg) && (
            <span className="text-white text-center text-opacity-15 font-bold text-base flex justify-center h-5">
              {game}
            </span>
          )} */}
      </div>
      {handMessages && (
        <div
          className={twMerge(
            styles.game,
            'flex h-5 items-center justify-center gap-3',
            isMobileView ? 'text-[10px]' : '',
          )}
        >
          {handMessages.map((info) => (
            <p
              key={info}
              className="rounded-md bg-ph-orange-secondary px-2.5 py-0.5 text-base font-bold uppercase text-white"
            >
              {info}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
export default Board;
